import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { vocabularyService } from '../../services/vocabularyService';
import { VocabularyWord, VocabularyFormData, Language } from '../../types/Vocabulary';
import PageHeader from '../common/PageHeader';
import VocabularyCard from './VocabularyCard';
import VocabularyForm from './VocabularyForm';
import VocabularyGrid from './VocabularyGrid';
import VocabularyIslands from './VocabularyIslands';
import VocabularyDetailModal from './VocabularyDetailModal';
import './VocabularyMatrix.css';

interface VocabularyMatrixProps {
  onToggleSidebar: () => void;
}

const VocabularyMatrix: React.FC<VocabularyMatrixProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingWord, setEditingWord] = useState<VocabularyWord | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLanguage, setFilterLanguage] = useState<Language | 'all'>('all');
  const [viewMode, setViewMode] = useState<'card' | 'grid' | 'islands'>('islands');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedWord, setSelectedWord] = useState<VocabularyWord | undefined>(undefined);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadWords();
    }
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadWords = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await vocabularyService.getUserWords(user.id.toString());
      
      if (result.success && result.words) {
        setWords(result.words);
      } else {
        setWords([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load vocabulary words');
      console.error('Error loading vocabulary words:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWord = () => {
    setEditingWord(undefined);
    setShowForm(true);
    setShowMenu(false);
  };

  const handleEditWord = (word: VocabularyWord) => {
    setEditingWord(word);
    setShowForm(true);
  };

  const handleDeleteWord = async (wordId: number) => {
    try {
      const result = await vocabularyService.deleteWord(wordId.toString());
      if (result.success) {
        await loadWords();
      } else {
        setError(result.message || 'Failed to delete word');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete word');
    }
  };

  const handleSaveWord = async (formData: VocabularyFormData) => {
    if (!user) return;

    try {
      if (editingWord) {
        // Update existing word
        const updateResult = await vocabularyService.updateWord({
          id: editingWord.id.toString(),
          word: formData.word,
          language: formData.language,
          pronunciation: formData.pronunciation,
          example_sentence: formData.example_sentence,
          notes: formData.notes,
          category: formData.category
        });

        if (!updateResult.success) {
          throw new Error(updateResult.message || 'Failed to update word');
        }

        // Update translations
        const existingTranslations = editingWord.translations || [];
        const newTranslations = formData.translations || [];

        // Delete removed translations
        for (const existing of existingTranslations) {
          if (!newTranslations.some(t => t.language === existing.language)) {
            await vocabularyService.deleteTranslation(editingWord.id.toString(), existing.id.toString());
          }
        }

        // Add or update translations
        for (const newTrans of newTranslations) {
          const existing = existingTranslations.find(t => t.language === newTrans.language);
          if (existing) {
            if (existing.translation !== newTrans.translation) {
              await vocabularyService.updateTranslation(
                editingWord.id.toString(),
                existing.id.toString(),
                { translation: newTrans.translation }
              );
            }
          } else {
            await vocabularyService.addTranslation(editingWord.id.toString(), newTrans);
          }
        }
      } else {
        // Create new word
        const result = await vocabularyService.createWord(user.id.toString(), formData);
        if (!result.success) {
          throw new Error(result.message || 'Failed to create word');
        }
      }

      setShowForm(false);
      setEditingWord(undefined);
      await loadWords();
    } catch (err: any) {
      throw err;
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingWord(undefined);
  };

  const handleGridCellClick = (word: VocabularyWord) => {
    setSelectedWord(word);
    setShowDetailModal(true);
  };

  const handleDetailModalClose = () => {
    setShowDetailModal(false);
    setSelectedWord(undefined);
  };

  const handleDetailModalEdit = (word: VocabularyWord) => {
    setEditingWord(word);
    setShowForm(true);
  };

  const handleIslandClick = (category: string) => {
    setSelectedCategory(category);
    setViewMode('grid');
  };

  const handleClearCategoryFilter = () => {
    setSelectedCategory(null);
  };

  const filteredWords = words.filter(word => {
    // Category filter (when island is selected)
    if (selectedCategory !== null) {
      const wordCategory = word.category || 'Uncategorized';
      if (wordCategory !== selectedCategory) {
        return false;
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesWord = word.word.toLowerCase().includes(query);
      const matchesPronunciation = word.pronunciation?.toLowerCase().includes(query);
      const matchesExample = word.example_sentence?.toLowerCase().includes(query);
      const matchesNotes = word.notes?.toLowerCase().includes(query);
      const matchesTranslations = word.translations?.some(t => 
        t.translation.toLowerCase().includes(query)
      );
      
      if (!matchesWord && !matchesPronunciation && !matchesExample && !matchesNotes && !matchesTranslations) {
        return false;
      }
    }

    // Language filter
    if (filterLanguage !== 'all' && word.language !== filterLanguage) {
      return false;
    }

    return true;
  });

  const rightActions = (
    <div className="vocabulary-menu-container" ref={menuRef}>
      <button
        className="vocabulary-menu-btn"
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
      >
        ⋮
      </button>
      {showMenu && (
        <div className="vocabulary-menu-dropdown">
          <div className="vocabulary-menu-section">
            <label className="vocabulary-menu-label">Search</label>
            <input
              type="text"
              placeholder="Search words, translations, examples..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="vocabulary-menu-search-input"
            />
          </div>
          <div className="vocabulary-menu-section">
            <label className="vocabulary-menu-label">Language</label>
            <select
              value={filterLanguage}
              onChange={(e) => setFilterLanguage(e.target.value as Language | 'all')}
              className="vocabulary-menu-language-filter"
            >
              <option value="all">All Languages</option>
              <option value="chinese">中文</option>
              <option value="french">Français</option>
              <option value="hindi">हिन्दी</option>
              <option value="english">English</option>
            </select>
          </div>
          <div className="vocabulary-menu-section">
            <label className="vocabulary-menu-label">View Mode</label>
            <div className="vocabulary-menu-view-toggle">
              <button
                className={`vocabulary-menu-view-toggle-btn ${viewMode === 'islands' ? 'active' : ''}`}
                onClick={() => {
                  setViewMode('islands');
                  setSelectedCategory(null);
                }}
              >
                Islands
              </button>
              <button
                className={`vocabulary-menu-view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                onClick={() => setViewMode('card')}
              >
                Card
              </button>
              <button
                className={`vocabulary-menu-view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                Grid
              </button>
            </div>
          </div>
          <div className="vocabulary-menu-section vocabulary-menu-section-action">
            <button onClick={handleAddWord} className="vocabulary-menu-add-btn">
              + Add Word
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (!user) {
    return (
      <div className="vocabulary-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="vocabulary-header-title">/vocabulary</h1>}
        />
        <div className="vocabulary-auth-required">
          <p>Please sign in to manage your vocabulary.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vocabulary-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="vocabulary-header-title">/vocabulary</h1>}
        rightActions={rightActions}
      />
      
      <div className="vocabulary-content">
        {error && (
          <div className="vocabulary-error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading ? (
          <div className="vocabulary-loading">Loading vocabulary...</div>
        ) : words.length === 0 ? (
          <div className="vocabulary-empty-state">
            <h2>No vocabulary words yet</h2>
            <p>Start by adding your first word to build your vocabulary matrix!</p>
            <button onClick={handleAddWord} className="vocabulary-empty-add-btn">
              Add Your First Word
            </button>
          </div>
        ) : viewMode === 'islands' ? (
          <>
            {selectedCategory && (
              <div className="vocabulary-category-filter">
                <span className="vocabulary-category-filter-label">
                  Showing: {selectedCategory}
                </span>
                <button
                  onClick={handleClearCategoryFilter}
                  className="vocabulary-category-filter-clear"
                >
                  Clear Filter
                </button>
              </div>
            )}
            {selectedCategory ? (
              filteredWords.length === 0 ? (
                <div className="vocabulary-empty-state">
                  <h2>No words found</h2>
                  <p>No words match the current filters.</p>
                </div>
              ) : (
                <VocabularyGrid
                  words={filteredWords}
                  onCellClick={handleGridCellClick}
                />
              )
            ) : (
              <VocabularyIslands
                words={words}
                onIslandClick={handleIslandClick}
              />
            )}
          </>
        ) : filteredWords.length === 0 ? (
          <div className="vocabulary-empty-state">
            <h2>No words found</h2>
            <p>No words match the current filters.</p>
          </div>
        ) : viewMode === 'card' ? (
          <div className="vocabulary-card-grid">
            {filteredWords.map(word => (
              <VocabularyCard
                key={word.id}
                word={word}
                onEdit={handleEditWord}
                onDelete={handleDeleteWord}
              />
            ))}
          </div>
        ) : (
          <VocabularyGrid
            words={filteredWords}
            onCellClick={handleGridCellClick}
          />
        )}
      </div>

      {showForm && (
        <VocabularyForm
          word={editingWord}
          onSave={handleSaveWord}
          onCancel={handleCancelForm}
        />
      )}

      {showDetailModal && selectedWord && (
        <VocabularyDetailModal
          word={selectedWord}
          onClose={handleDetailModalClose}
          onEdit={handleDetailModalEdit}
          onDelete={handleDeleteWord}
        />
      )}
    </div>
  );
};

export default VocabularyMatrix;

